/**
 * Utilities for handling magic link invitations
 */

/**
 * Parse invitation input to extract token
 * Accepts full URL or just the token
 * 
 * @param input - Full URL or token string
 * @returns Extracted token string
 * 
 * @example
 * parseInvitationInput('https://app.getzube.com/auth/signup?magic_link_token=abc123')
 * // Returns: 'abc123'
 * 
 * parseInvitationInput('app.getzube.com/auth/signup?magic_link_token=abc123')
 * // Returns: 'abc123'
 * 
 * parseInvitationInput('abc123')
 * // Returns: 'abc123'
 */
export function parseInvitationInput(input: string): string {
  const trimmed = input.trim();

  // Extract token from URL parameter if present
  // Matches both ?magic_link_token= and &magic_link_token=
  const tokenMatch = trimmed.match(/[?&]magic_link_token=([^&\s]+)/);
  if (tokenMatch && tokenMatch[1]) {
    return tokenMatch[1];
  }

  // Otherwise, treat entire input as token
  return trimmed;
}

/**
 * Validate basic token format
 * Base64url tokens are typically 40-50 characters and use A-Za-z0-9_-
 * 
 * @param token - Token string to validate
 * @returns true if token format appears valid
 * 
 * @example
 * isValidTokenFormat('dGVzdC10b2tlbi0xMjM0NTY3ODkwYWJjZGVm')
 * // Returns: true
 * 
 * isValidTokenFormat('invalid')
 * // Returns: false
 */
export function isValidTokenFormat(token: string): boolean {
  // Base64url tokens are typically ~43 characters (32 bytes encoded)
  // Allow range of 20-100 to be flexible
  // Must only contain base64url characters: A-Za-z0-9_-
  return (
    token.length > 20 &&
    token.length < 100 &&
    /^[A-Za-z0-9_-]+$/.test(token)
  );
}

/**
 * Get user-friendly error message for invitation errors
 * 
 * @param error - Error message from backend
 * @returns User-friendly error message
 */
export function getInvitationErrorMessage(error: string): string {
  const lowerError = error.toLowerCase();

  if (lowerError.includes('expired')) {
    return 'This invitation has expired. Please contact your school for a new link.';
  }

  if (lowerError.includes('domain') && lowerError.includes('match')) {
    return "Your email domain doesn't match this invitation's requirements. Please check with your teacher.";
  }

  if (lowerError.includes('already') && lowerError.includes('used')) {
    return 'This invitation has already been used.';
  }

  if (lowerError.includes('invalid') || lowerError.includes('not found')) {
    return 'Invalid invitation link. Please check the link and try again.';
  }

  // Generic fallback
  return 'Unable to apply invitation. Please try again or contact support.';
}

